import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AccountCard, Badge, Button, ConnectivityBanner, ErrorBoundaryCore,
  useOnlineStatus, ConnectionState,
} from '@parsina/framework';
import { GRADIENT_SIDEBAR } from '@parsina/framework/tokens';
import {
  Building2, Check, Coins, Earth, Globe, LayoutDashboard, LogOut, MessageCircle, Presentation, Store,
  UsersRound,
} from 'lucide-react';

import { useTranslation } from '../i18n';
import { LOCALES, offeredLocaleIds } from '../i18n';
import { clearTokens, logout } from '../lib/auth';
import { ENV_NAME, APP_VERSION } from '../lib/runtimeEnv';
import { allows } from '../lib/access';
import useNewDemoCount from '../screens/DemoRequests/useNewDemoCount';
// The white mark, by the brand's asset pack (src/lib/brandAssets.js imports it
// with `url:`, so Parcel hands back a URL string rather than an inlined module).
import { brandAsset } from '../lib/brandAssets';
import SystemState from './SystemState';

/**
 * The back-office shell.
 *
 * Same sidebar language as the clinic panel — deep-azure gradient rail, brand
 * gradient on the active pill — so an operator moving between the two is not
 * relearning the furniture. Deliberately sparse: this panel has two
 * destinations, and a nav built for twelve would imply features that do not
 * exist.
 *
 * THE GRID IS DIRECTION-AGNOSTIC. `grid-template-columns: [rail] [main]` puts
 * the rail in the first column, which is the inline-start — the right in Persian,
 * the left in English — with no code path and no `dir` anywhere. The previous
 * shell set `dir="rtl"` on its own root, which is what made English impossible:
 * every logical property inside it kept resolving RTL no matter what the document
 * said (DESIGN-CONCEPT §16.3).
 */

/**
 * Every destination, and the gate it sits behind (`me.access`, computed by the
 * admin BFF). A sales/support user holding only a CRM role sees exactly one
 * item; a platform_admin without one does not see «درخواست‌های دمو». Until
 * `me` answers, the rail shows no destinations rather than guessing.
 */
export const NAV = [
  { path: '/dashboard', labelKey: 'nav.items.dashboard', Icon: LayoutDashboard, requires: 'platform' },
  { path: '/clinics', labelKey: 'nav.items.clinics', Icon: Building2, requires: 'platform' },
  { path: '/ai-pricing', labelKey: 'nav.items.aiPricing', Icon: Coins, requires: 'platform' },
  { path: '/marketplace', labelKey: 'nav.items.marketplace', Icon: Store, requires: 'platform' },
  { path: '/whatsapp', labelKey: 'nav.items.whatsapp', Icon: MessageCircle, requires: 'platform' },
  { path: '/doctor-pages', labelKey: 'nav.items.doctorPages', Icon: Globe, requires: 'platform' },
  // The rules of each jurisdiction (PM-0218) — profiles, history, insurer catalogue.
  { path: '/countries', labelKey: 'nav.items.countries', Icon: Earth, requires: 'platform' },
  // Support: one person's two sign-in accounts, a record's account released (PM-0536, PM-0546).
  { path: '/patient-accounts', labelKey: 'nav.items.patientAccounts', Icon: UsersRound, requires: 'platform' },
  { path: '/demo-requests', labelKey: 'nav.items.demoRequests', Icon: Presentation, requires: 'crm', badge: 'demoRequests' },
];

/** The destinations `access` opens, in rail order. None while access is unknown. */
export const navFor = (access) => (access ? NAV.filter((item) => allows(access, item.requires)) : []);

/** The Compact tier's icon rail (§4.1: 769–1180px). */
const COMPACT_QUERY = '(max-width: 1180px)';

function useCompact() {
  const [compact, setCompact] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(COMPACT_QUERY);
    const sync = () => setCompact(mq.matches);
    sync();
    if (mq.addEventListener) mq.addEventListener('change', sync);
    else mq.addListener(sync);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', sync);
      else mq.removeListener(sync);
    };
  }, []);
  return compact;
}

/**
 * One sidebar destination.
 *
 * A real `<a href>` via `asChild`, not a button with an onClick, so
 * middle-click, ⌘-click and "copy link" work — an operator who keeps the estate
 * open in one tab and a clinic in another cannot do that with a button. The
 * selected look comes from the kit's `variant="nav"` reading `data-active`, so
 * this component holds no colours of its own.
 */
function NavItem({
  path, label, Icon, active, compact, badge, badgeLabel,
}) {
  const { formatNumber } = useTranslation();
  // The count is announced as words (`badgeLabel`), the digits are decoration.
  const shown = badge > 99 ? `${formatNumber(99)}+` : formatNumber(badge);
  return (
    <Button
      asChild
      variant="nav"
      data-active={active ? 'true' : undefined}
      className={compact ? 'justify-center px-0' : 'gap-2.5 px-3'}
      data-testid={`nav-${path.slice(1)}`}
    >
      <Link
        to={path}
        // The current page is announced, not merely tinted: colour alone does
        // not tell a screen-reader user where they are (§12.1).
        aria-current={active ? 'page' : undefined}
        title={compact ? label : undefined}
      >
        <span className="relative inline-flex">
          <Icon size={18} aria-hidden />
          {compact && badge > 0 ? (
            <span
              aria-hidden
              className="absolute -top-1 -end-1 h-2 w-2 rounded-full bg-[var(--chip-warning-ink)]"
              data-testid={`nav-badge-dot-${path.slice(1)}`}
            />
          ) : null}
        </span>
        {compact ? <span className="sr-only">{label}</span> : label}
        {badge > 0 ? (
          <>
            {!compact ? (
              <Badge variant="count" className="ms-auto" aria-hidden data-testid={`nav-badge-${path.slice(1)}`}>
                {shown}
              </Badge>
            ) : null}
            <span className="sr-only">{` (${badgeLabel})`}</span>
          </>
        ) : null}
      </Link>
    </Button>
  );
}

export default function AdminShell({ children, me, access }) {
  const location = useLocation();
  const { t, locale, setLocale, preloadLocale } = useTranslation();
  const compact = useCompact();
  const { state, recovered, acknowledgeRecovery } = useOnlineStatus();
  // The «unanswered demo requests» badge (§2 D8): polled only for a caller
  // who can open the queue; hidden on any failure.
  const newDemoCount = useNewDemoCount({ enabled: !!access?.crm });
  const badges = { demoRequests: newDemoCount };

  const railWidth = compact ? '4.25rem' : '15rem';

  // Language rows rather than a segmented control: `AccountCard`'s menu takes
  // `{label, icon, onClick}` items, and a picker that has to be a control is a
  // reason to change the kit, not to smuggle a node in. Two named rows with a
  // tick on the active one is what a menu does anyway, and each row's label is
  // the language's ENDONYM — «فارسی» stays «فارسی» in the English UI, because the
  // person who needs it is the one who cannot read the current language.
  // Only the languages the brand offers; a brand that offers one gets no rows.
  const offered = offeredLocaleIds();
  const languageItems = (offered.length > 1 ? offered : []).map((id) => ({
    key: `lang-${id}`,
    label: LOCALES[id].label,
    icon: id === locale ? <Check size={16} /> : <span style={{ width: 16 }} />,
    onClick: () => setLocale(id),
    onMouseEnter: () => preloadLocale(id),
    'data-testid': `shell-lang-${id}`,
  }));

  const accountItems = [
    ...languageItems,
    {
      key: 'sign-out',
      label: t('nav.account.signOut'),
      icon: <LogOut size={16} />,
      tone: 'destructive',
      // A separator and the destructive tone, so nobody reaches it on the way to
      // switching language.
      separatorBefore: languageItems.length > 0,
      onClick: () => { clearTokens(); logout(); },
      'data-testid': 'shell-logout',
    },
  ];

  return (
    <div
      className="grid h-screen"
      style={{ gridTemplateColumns: `${railWidth} minmax(0, 1fr)` }}
    >
      {/* Skip link: the first thing in the tab order, because the alternative is
          tabbing through the whole rail on every page (§12.2). */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-[1200] focus:m-2 focus:rounded-lg focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-foreground focus:shadow-md"
        data-testid="shell-skip"
      >
        {t('nav.skipToContent')}
      </a>

      <aside
        className="flex min-w-0 flex-col py-5"
        style={{ background: GRADIENT_SIDEBAR }}
      >
        <div className={compact ? 'flex justify-center pb-6' : 'flex items-center gap-2.5 px-5 pb-6'}>
          <img src={brandAsset('logoWhite')} alt="" aria-hidden className="h-9 w-auto shrink-0 object-contain" />
          {!compact && (
            <div className="min-w-0">
              <div className="truncate text-base font-extrabold text-white">
                {t('common.brand.name')}
              </div>
              <div className="truncate text-xs text-white/55">{t('nav.product')}</div>
            </div>
          )}
        </div>

        <nav aria-label={t('nav.primary')} className="flex flex-col gap-1 px-2.5">
          {navFor(access).map(({
            path, labelKey, Icon, badge,
          }) => (
            <NavItem
              key={path}
              path={path}
              label={t(labelKey)}
              Icon={Icon}
              badge={badge ? badges[badge] : null}
              badgeLabel={badge && badges[badge] ? t(`nav.badge.${badge}`, { count: badges[badge] }) : ''}
              // A second-level page keeps its parent section highlighted (§3.1),
              // so /clinics/:id still lights «کلینیک‌ها».
              active={location.pathname === path || location.pathname.startsWith(`${path}/`)}
              compact={compact}
            />
          ))}
        </nav>

        <div className="flex-1" />

        {/* Which deployment this is. An ops panel that looks identical on beta1
            and on staging is one where an operator eventually suspends the wrong
            clinic. LTR: an environment name and a version are identifiers. */}
        {!compact && (
          <div
            className="px-5 pb-2 font-mono text-[11px] text-white/45"
            dir="ltr"
            style={{ unicodeBidi: 'isolate' }}
            data-testid="shell-env"
          >
            {ENV_NAME} · v{APP_VERSION}
          </div>
        )}

        {/* No `credit`: پارسه is a doctor's and a clinic's currency, and a
            platform operator holds none. Omitting it means the zone does not
            render, so there is no number here to be wrong about. */}
        <AccountCard
          user={{
            name: me?.name || me?.username || t('nav.account.unknown'),
            subtitle: me?.username || undefined,
          }}
          items={accountItems}
          collapsed={compact}
          menuLabel={t('nav.account.menuLabel')}
          data-testid="shell-account"
        />
      </aside>

      <main id="main" className="flex min-w-0 flex-col overflow-hidden bg-background">
        <ConnectivityBanner
          state={state}
          recovered={recovered}
          onAcknowledgeRecovery={acknowledgeRecovery}
          offlineLabel={t('errors.connectivity.offline')}
          degradedLabel={t('errors.connectivity.degraded')}
          restoredLabel={t('errors.connectivity.restored')}
        />
        {/*
          Keyed on the pathname so a screen that throws costs that screen and
          navigating away recovers: React latches a boundary permanently
          otherwise, and the operator is left on a dead panel until they reload.
        */}
        <ErrorBoundaryCore
          resetKey={location.pathname}
          renderFallback={(error, reset) => (
            <SystemState
              tone="danger"
              titleKey="errors.system.crash.title"
              adviceKey="errors.system.crash.advice"
              actions={[
                // Re-render in place first: a screen that threw on transient data
                // usually comes back, and that is cheaper than a full reload for
                // an operator mid-ticket.
                { key: 'retry', label: t('common.actions.retry'), onClick: reset },
                {
                  key: 'reload',
                  label: t('common.actions.reload'),
                  variant: 'secondary',
                  onClick: () => window.location.reload(),
                },
              ]}
              testId="system-crash"
            />
          )}
        >
          {children}
        </ErrorBoundaryCore>
      </main>
    </div>
  );
}

export { ConnectionState };
